import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  handleNotificationAction,
  getNotificationIcon,
  formatNotificationTime,
  Notification
} from '../services/notificationService';
import '../styles/components/notificationBell.css';

interface NotificationBellProps {
  onNavigateToCollection?: (collectionId: number) => void;
  onNavigateToGroup?: (groupId: number) => void;
  onUnreadCountChange?: (count: number) => void;
  larger?: boolean; // Make the bell icon larger
  hidden?: boolean; // Hide the bell visually but still poll for notifications
  style?: React.CSSProperties; // Allow inline styles for hiding
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  onNavigateToCollection,
  onNavigateToGroup,
  onUnreadCountChange,
  larger = false,
  hidden = false,
  style
}) => {
  const { token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  // Fetch unread count periodically
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const count = await getUnreadCount(token);
      setUnreadCount(count);
      onUnreadCountChange?.(count);
    } catch (error: any) {
      // Log more details for debugging network issues after device wake
      console.error('Failed to fetch unread count:', error?.message || error, error?.name || '');
    }
  }, [token, onUnreadCountChange]);

  // Fetch notifications when dropdown opens
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await getNotifications(token, { limit: 20 });
      setNotifications(response.notifications);
      setUnreadCount(response.unread_count);
      onUnreadCountChange?.(response.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token, onUnreadCountChange]);

  // Poll for unread count every 30 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Notify parent when unread count changes (e.g., after marking as read)
  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleBellClick = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if not already
    if (!notification.is_read && token) {
      try {
        await markAsRead(token, notification.id);
        setNotifications(prev =>
          prev.map(n =>
            n.id === notification.id ? { ...n, is_read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    // Navigate based on notification type
    if (notification.collection_id && onNavigateToCollection) {
      onNavigateToCollection(notification.collection_id);
      setIsOpen(false);
    } else if (notification.group_id && onNavigateToGroup) {
      onNavigateToGroup(notification.group_id);
      setIsOpen(false);
    }
  };

  const handleAction = async (
    e: React.MouseEvent,
    notification: Notification,
    action: 'accept' | 'decline'
  ) => {
    e.stopPropagation();
    if (!token) return;

    setActionLoading(notification.id);
    try {
      await handleNotificationAction(token, notification.id, action);
      setNotifications(prev =>
        prev.map(n =>
          n.id === notification.id
            ? { ...n, action_status: action === 'accept' ? 'accepted' : 'declined', is_read: true }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to handle notification action:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    if (!token) return;

    try {
      await deleteNotification(token, notificationId);
      const deleted = notifications.find(n => n.id === notificationId);
      const newNotifications = notifications.filter(n => n.id !== notificationId);
      setNotifications(newNotifications);
      if (deleted && !deleted.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      // Auto-close modal if no notifications left
      if (newNotifications.length === 0) {
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleMarkAllRead = async () => {
    if (!token) return;

    try {
      await markAllAsRead(token);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      // Auto-close modal after marking all as read
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // When hidden, render an invisible div that still runs effects to poll for notifications
  if (hidden) {
    return <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} />;
  }

  return (
    <div className="notification-bell-container" style={style}>
      <button
        ref={bellRef}
        className={`notification-bell-button ${unreadCount > 0 ? 'has-unread' : ''} ${larger ? 'larger' : ''}`}
        onClick={handleBellClick}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <svg
          width={larger ? "24" : "20"}
          height={larger ? "24" : "20"}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div ref={dropdownRef} className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllRead}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">
                <div className="loading-spinner" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <span className="empty-icon">🔔</span>
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">{notification.title}</div>
                    {notification.message && (
                      <div className="notification-message">{notification.message}</div>
                    )}
                    <div className="notification-meta">
                      <span className="notification-time">
                        {formatNotificationTime(notification.created_at)}
                      </span>
                      {notification.from_user?.name && (
                        <span className="notification-from">
                          from {notification.from_user.name}
                        </span>
                      )}
                    </div>

                    {notification.requires_action && notification.action_status === 'pending' && (
                      <div className="notification-actions">
                        <button
                          className="action-btn accept"
                          onClick={(e) => handleAction(e, notification, 'accept')}
                          disabled={actionLoading === notification.id}
                        >
                          {actionLoading === notification.id ? '...' : 'Accept'}
                        </button>
                        <button
                          className="action-btn decline"
                          onClick={(e) => handleAction(e, notification, 'decline')}
                          disabled={actionLoading === notification.id}
                        >
                          Decline
                        </button>
                      </div>
                    )}

                    {notification.action_status && notification.action_status !== 'pending' && (
                      <div className={`notification-status ${notification.action_status}`}>
                        {notification.action_status === 'accepted' ? '✓ Accepted' : '✗ Declined'}
                      </div>
                    )}
                  </div>

                  <button
                    className="notification-delete"
                    onClick={(e) => handleDelete(e, notification.id)}
                    aria-label="Delete notification"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
